import { useState } from "react";
import { useSelector } from "react-redux";
import usePageLoader from '../hooks/usePageLoader';
import { ShimmerStatCard, ShimmerBox, ShimmerCircle, ShimmerListItem } from '../components/common/Shimmer';
import { useNavigate, useParams } from "react-router-dom";
import {
  FiLink, FiFileText, FiMessageCircle, FiThumbsUp, FiThumbsDown,
  FiAlertCircle, FiCopy, FiCheck, FiTrendingUp, FiTrendingDown,
  FiUsers, FiZap, FiExternalLink, FiArrowRight, FiActivity,
  FiInbox, FiUserCheck, FiCheckCircle,
} from "react-icons/fi";
import { SiNotion, SiConfluence, SiZendesk } from "react-icons/si";
import { FaYoutube } from "react-icons/fa";
import { BiConversation } from "react-icons/bi";
import { MdOutlineTextFields } from "react-icons/md";
import toast from "react-hot-toast";

/* ─── Mock data ─── */
const MOCK_CONVERSATIONS = [
  { id: 1, user: "Arun Kumar",   message: "What pricing plans do you offer?",           time: "2 min ago",  status: "Active" },
  { id: 2, user: "Priya Sharma", message: "How do I train on a PDF file?",              time: "15 min ago", status: "Closed" },
  { id: 3, user: "Rahul Verma",  message: "Does it support Notion integration?",        time: "1 hr ago",   status: "Closed" },
  { id: 4, user: "Meena Iyer",   message: "Can I embed this on Webflow?",               time: "3 hrs ago",  status: "Closed" },
  { id: 5, user: "Suresh Nair",  message: "What is the message limit on Starter plan?", time: "5 hrs ago",  status: "Closed" },
];

const LIVE_SESSIONS = [
  { id: "V1742746063", country: "🇮🇳", ip: "157.20.250.156", duration: "07:30", msgs: 14 },
  { id: "V1742746098", country: "🇺🇸", ip: "104.28.12.91",  duration: "03:15", msgs: 8  },
  { id: "V1742746112", country: "🇬🇧", ip: "81.2.69.142",   duration: "01:44", msgs: 4  },
];

function OverviewSkeleton() {
  return (
    <div className="flex-1 overflow-y-auto p-6 flex gap-6 items-start">
      <div className="flex-1 min-w-0 flex flex-col gap-6">
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <ShimmerStatCard key={i} />)}
        </div>
        <div className="grid grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white border rounded-xl p-4 space-y-3">
              <ShimmerBox h="h-3" w="w-20" />
              {[...Array(3)].map((_, j) => <ShimmerBox key={j} h="h-3" w={j % 2 === 0 ? 'w-full' : 'w-3/4'} />)}
              <ShimmerBox h="h-3" w="w-24" />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-4">
          {[...Array(2)].map((_, i) => <ShimmerStatCard key={i} />)}
        </div>
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b">
            <ShimmerBox h="h-4" w="w-36" />
            <ShimmerBox h="h-3" w="w-16" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-5">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="border border-gray-200 rounded-xl p-4 space-y-3">
                <ShimmerBox h="h-8" w="w-8" rounded="rounded-lg" />
                <ShimmerBox h="h-6" w="w-12" />
                <ShimmerBox h="h-1.5" w="w-full" rounded="rounded-full" />
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b">
            <ShimmerBox h="h-4" w="w-44" />
          </div>
          {[...Array(5)].map((_, i) => <ShimmerListItem key={i} />)}
        </div>
      </div>
      <div className="w-80 shrink-0 flex flex-col gap-4">
        <div className="bg-white border rounded-xl p-5 space-y-3">
          <ShimmerBox h="h-4" w="w-32" />
          <ShimmerBox h="h-3" w="w-3/4" />
          <ShimmerBox h="h-20" w="w-full" rounded="rounded-lg" />
          <ShimmerBox h="h-8" w="w-full" rounded="rounded-lg" />
          <ShimmerBox h="h-8" w="w-full" rounded="rounded-lg" />
        </div>
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b"><ShimmerBox h="h-4" w="w-24" /></div>
          {[...Array(3)].map((_, i) => <ShimmerListItem key={i} />)}
        </div>
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b"><ShimmerBox h="h-4" w="w-28" /></div>
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3 border-b last:border-0">
              <ShimmerBox h="h-8" w="w-8" rounded="rounded-lg" className="shrink-0" />
              <ShimmerBox h="h-3" w="w-24" />
              <ShimmerBox h="h-5" w="w-16" rounded="rounded-full" className="ml-auto" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Overview() {
  const { chatbotId } = useParams();
  const { selected } = useSelector((state) => state.chatbots);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const loading = usePageLoader();

  const id = chatbotId || selected?.chatbot_id || "bot-001";

  const embedScript = `<script>
  (function(w,d,s,o){
    w.NexaGPT=o;
    var j=d.createElement(s);
    j.src='https://app.nexagpt.ai/widget.js';
    j.setAttribute('data-id','${id}');
    d.head.appendChild(j);
  })(window,document,'script','nexagpt');
</script>`;

  const handleCopy = () => {
    navigator.clipboard.writeText(embedScript);
    setCopied(true);
    toast.success("Embed code copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const SOURCES = [
    { key: "website",    label: "Website",       icon: <FiLink size={17} className="text-blue-500" />,               bg: "bg-blue-50",   trained: 6, pending: 2, route: `/sources/website/${id}` },
    { key: "files",      label: "Files",         icon: <FiFileText size={17} className="text-orange-500" />,         bg: "bg-orange-50", trained: 4, pending: 2, route: `/sources/files/${id}` },
    { key: "raw",        label: "Raw Text",      icon: <MdOutlineTextFields size={17} className="text-indigo-500" />,bg: "bg-indigo-50", trained: 3, pending: 1, route: `/sources/raw/${id}` },
    { key: "notion",     label: "Notion",        icon: <SiNotion size={15} className="text-gray-800" />,             bg: "bg-gray-100",  trained: 3, pending: 2, route: `/sources/notion/${id}` },
    { key: "youtube",    label: "YouTube",       icon: <FaYoutube size={17} className="text-red-500" />,             bg: "bg-red-50",    trained: 2, pending: 1, route: `/sources/youtube/${id}` },
    { key: "confluence", label: "Confluence",    icon: <SiConfluence size={15} className="text-blue-700" />,         bg: "bg-blue-50",   trained: 3, pending: 1, route: `/sources/confluence/${id}` },
    { key: "zendesk",    label: "Zendesk",       icon: <SiZendesk size={15} className="text-green-600" />,           bg: "bg-green-50",  trained: 3, pending: 2, route: `/sources/zendesk/${id}` },
  ];

  const totalTrained = SOURCES.reduce((s, src) => s + src.trained, 0);
  const totalPending = SOURCES.reduce((s, src) => s + src.pending, 0);

  return (
    <div className="-m-6 flex flex-col" style={{ height: "calc(100vh - 4rem)" }}>

      {/* ── Page Header ── */}
      <div className="shrink-0 bg-white border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Overview</h1>
          <p className="text-sm text-gray-500 mt-0.5">Visitors, conversations, leads, and training status at a glance</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5 text-xs bg-green-100 text-green-700 px-3 py-1.5 rounded-full font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            Active
          </span>
          <button
            onClick={() => navigate(`/chatbot/demo`)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition"
          >
            <FiExternalLink size={14} /> Try Demo
          </button>
        </div>
      </div>

      {/* ── Scrollable body ── */}
      {loading ? <OverviewSkeleton /> : <div className="flex-1 overflow-y-auto p-6 flex gap-6 items-start">

        {/* ── LEFT COLUMN ── */}
        <div className="flex-1 min-w-0 flex flex-col gap-6">

          {/* ── KPI Metrics Row ── */}
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
            <StatCard icon={FiUsers}         title="Total Visitors"     value="1,284" trend="+12%"  up />
            <StatCard icon={BiConversation}  title="Conversations"      value="347"   trend="+8%"   up />
            <StatCard icon={FiMessageCircle} title="Messages Sent"      value="2,940" trend="+21%"  up />
            <StatCard icon={FiZap}           title="Avg. Response Time" value="1.2s"  trend="-0.3s" up />
          </div>

          {/* ── Quick Status Row: Live + Inbox + Leads ── */}
          <div className="grid grid-cols-3 gap-4">
            {/* Live Sessions */}
            <div
              onClick={() => navigate(`/monitoring/${id}`)}
              className="bg-white border rounded-xl p-4 cursor-pointer hover:shadow-sm transition group"
            >
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Live Now</p>
                <span className="flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                  {LIVE_SESSIONS.length} active
                </span>
              </div>
              <div className="space-y-2">
                {LIVE_SESSIONS.map((s) => (
                  <div key={s.id} className="flex items-center justify-between text-xs">
                    <span className="text-gray-600 font-mono">{s.country} {s.ip}</span>
                    <span className="text-gray-400">{s.msgs} msgs</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-primary mt-3 flex items-center gap-1 group-hover:underline font-medium">
                View Monitoring <FiArrowRight size={11} />
              </p>
            </div>

            {/* Inbox Status */}
            <div
              onClick={() => navigate(`/inbox/${id}`)}
              className="bg-white border rounded-xl p-4 cursor-pointer hover:shadow-sm transition group"
            >
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Inbox</p>
                <FiInbox size={14} className="text-gray-400" />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-900">4 open</span>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Open</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">2 closed</span>
                  <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium">Closed</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">3 unread</span>
                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">Unread</span>
                </div>
              </div>
              <p className="text-xs text-primary mt-3 flex items-center gap-1 group-hover:underline font-medium">
                View Inbox <FiArrowRight size={11} />
              </p>
            </div>

            {/* Leads Status */}
            <div
              onClick={() => navigate(`/leads/${id}`)}
              className="bg-white border rounded-xl p-4 cursor-pointer hover:shadow-sm transition group"
            >
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Leads</p>
                <FiUserCheck size={14} className="text-gray-400" />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-900">15 total</span>
                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">All time</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">2 new today</span>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">New</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">4 converted</span>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">26%</span>
                </div>
              </div>
              <p className="text-xs text-primary mt-3 flex items-center gap-1 group-hover:underline font-medium">
                View Leads <FiArrowRight size={11} />
              </p>
            </div>
          </div>

          {/* ── Feedback Row ── */}
          <div className="grid grid-cols-2 gap-4">
            <StatCard icon={FiThumbsUp}   title="Positive Feedback" value="83%" trend="+5%" up iconColor="text-green-500" />
            <StatCard icon={FiThumbsDown} title="Negative Feedback" value="17%" trend="-5%" up iconColor="text-red-400" />
          </div>

          {/* ── Training Sources ── */}
          <div className="bg-white border rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <div>
                <h2 className="font-semibold text-gray-900">Training Sources</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  <span className="text-green-600 font-medium">{totalTrained} trained</span>
                  {totalPending > 0 && <span className="text-yellow-600 font-medium"> · {totalPending} pending</span>}
                  {" "}across {SOURCES.length} sources
                </p>
              </div>
              <button
                onClick={() => navigate(`/sources/website/${id}`)}
                className="flex items-center gap-1 text-xs text-primary hover:underline font-medium"
              >
                Manage All <FiArrowRight size={12} />
              </button>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-5">
              {SOURCES.map((src) => {
                const total = src.trained + src.pending;
                const pct = total > 0 ? Math.round((src.trained / total) * 100) : 0;
                return (
                  <div key={src.key} className="border border-gray-200 rounded-xl p-4 flex flex-col gap-3">
                    <div className="flex items-start justify-between">
                      <div className={`w-8 h-8 rounded-lg ${src.bg} flex items-center justify-center`}>
                        {src.icon}
                      </div>
                      <button
                        onClick={() => navigate(src.route)}
                        className="text-[11px] text-gray-400 hover:text-primary flex items-center gap-0.5 transition"
                      >
                        Manage <FiArrowRight size={10} />
                      </button>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">{src.label}</p>
                      <p className="text-2xl font-bold text-gray-900 mt-0.5">{src.trained}</p>
                    </div>
                    <div>
                      <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-green-500 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-[10px] text-green-600 font-medium">{src.trained} trained</span>
                        {src.pending > 0 && (
                          <span className="text-[10px] text-yellow-600 font-medium">{src.pending} pending</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Recent Conversations ── */}
          <div className="bg-white border rounded-xl">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h2 className="font-semibold text-gray-900">Recent Conversations</h2>
              <button onClick={() => navigate(`/inbox/${id}`)} className="text-sm text-primary hover:underline">
                View All →
              </button>
            </div>
            <div className="divide-y">
              {MOCK_CONVERSATIONS.map((conv) => (
                <div key={conv.id} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
                      {conv.user[0]}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{conv.user}</p>
                      <p className="text-xs text-gray-500 truncate max-w-xs">{conv.message}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-400">{conv.time}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      conv.status === "Active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                    }`}>
                      {conv.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* ── RIGHT COLUMN (sticky) ── */}
        <div className="w-80 shrink-0 sticky top-0 flex flex-col gap-4">

          {/* Embed Chatbot */}
          <div className="bg-white border rounded-xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Embed Chatbot</h2>
              <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full flex items-center gap-1">
                <FiAlertCircle size={11} /> Training
              </span>
            </div>

            <p className="text-xs text-gray-500 leading-relaxed">
              Paste this script just before the{" "}
              <code className="bg-gray-100 px-1 rounded">&lt;/body&gt;</code> tag of your website.
            </p>

            <div className="relative bg-gray-900 rounded-lg overflow-hidden">
              <pre className="text-[11px] text-green-400 p-3 overflow-x-auto leading-relaxed whitespace-pre-wrap">{embedScript}</pre>
              <button
                onClick={handleCopy}
                className={`absolute top-2 right-2 flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-medium transition ${
                  copied ? "bg-green-500 text-white" : "bg-white/10 text-white hover:bg-white/20"
                }`}
              >
                {copied ? <FiCheck size={11} /> : <FiCopy size={11} />}
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>

            <div className="bg-gray-50 rounded-lg px-3 py-2.5">
              <p className="text-[10px] text-gray-400 uppercase tracking-wide font-medium">Chatbot ID</p>
              <p className="text-xs font-mono text-gray-700 mt-0.5">{id}</p>
            </div>

            <div className="flex flex-col gap-2 pt-1">
              <button
                onClick={() => navigate(`/chatbot/demo`)}
                className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-primary text-white text-sm rounded-lg hover:opacity-90 transition"
              >
                <FiExternalLink size={13} /> Open Live Demo
              </button>
              <button
                onClick={() => navigate(`/sources/website/${id}`)}
                className="flex items-center justify-center gap-2 w-full px-4 py-2 border text-sm rounded-lg hover:bg-gray-50 transition text-gray-700"
              >
                <FiLink size={13} /> Manage Content
              </button>
            </div>
          </div>

          {/* Leads Snapshot */}
          <div className="bg-white border rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900">Leads Snapshot</h2>
              <button onClick={() => navigate(`/leads/${id}`)} className="text-xs text-primary hover:underline font-medium flex items-center gap-0.5">
                View All <FiArrowRight size={11} />
              </button>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                    <FiUsers size={13} className="text-primary" />
                  </div>
                  <span className="text-sm text-gray-700">Total Leads</span>
                </div>
                <span className="text-sm font-bold text-gray-900">15</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                    <FiTrendingUp size={13} className="text-blue-500" />
                  </div>
                  <span className="text-sm text-gray-700">New Today</span>
                </div>
                <span className="text-sm font-bold text-blue-600">2</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center">
                    <FiCheckCircle size={13} className="text-green-500" />
                  </div>
                  <span className="text-sm text-gray-700">Converted</span>
                </div>
                <span className="text-sm font-bold text-green-600">4 (26%)</span>
              </div>
            </div>
          </div>

          {/* Integrations Status */}
          <div className="bg-white border rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900">Integrations</h2>
              <button onClick={() => navigate(`/integrations/${id}`)} className="text-xs text-primary hover:underline font-medium flex items-center gap-0.5">
                Manage <FiArrowRight size={11} />
              </button>
            </div>
            <div className="space-y-2.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded bg-green-600 flex items-center justify-center text-white text-[9px] font-bold">PD</div>
                  <span className="text-sm text-gray-700">Pipedrive</span>
                </div>
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Connected</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded bg-orange-500 flex items-center justify-center text-white text-[9px] font-bold">HS</div>
                  <span className="text-sm text-gray-700">HubSpot</span>
                </div>
                <button
                  onClick={() => navigate(`/integrations/${id}`)}
                  className="text-xs text-primary hover:underline font-medium"
                >
                  Connect
                </button>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded bg-indigo-600 flex items-center justify-center text-white text-[9px] font-bold">SL</div>
                  <span className="text-sm text-gray-700">Slack</span>
                </div>
                <button
                  onClick={() => navigate(`/integrations/${id}`)}
                  className="text-xs text-primary hover:underline font-medium"
                >
                  Connect
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-3">1 of 18 integrations connected</p>
          </div>

        </div>

      </div>}
    </div>
  );
}

function StatCard({ icon: Icon, title, value, trend, up, iconColor = "text-primary" }) {
  return (
    <div className="bg-white border rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-gray-500 font-medium">{title}</p>
        <Icon className={iconColor} size={16} />
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <div className={`flex items-center gap-1 mt-1 text-xs font-medium ${up ? "text-green-600" : "text-red-500"}`}>
        {up ? <FiTrendingUp size={12} /> : <FiTrendingDown size={12} />}
        {trend} <span className="text-gray-400 font-normal">vs last week</span>
      </div>
    </div>
  );
}
